import React, { useMemo } from 'react'
import { Card, CardContent, useTheme } from '@mui/material'
import ReactECharts from 'echarts-for-react'
import { type KeyStatSurveyItem } from '../../../../../models/Report'
import { formatTooltip, getFilteredOptions, buildBarSeries, getBarChartColors } from './helper'
import type { OptionType } from './helper'
import { useReportsContext } from '../../../ReportsContext'

export interface BarChartProps {
  titleComponent: React.ReactNode
  statisticBox: React.ReactNode
  chartKey: number
  showPercents?: boolean
  titles?: string[]
  report?: KeyStatSurveyItem
  showSlice?: boolean
}

const buildCustomLineSeries = (titles: string[], options: OptionType[], barColors: string[]): any => {
  const customData = options.map((item, i) => {
    const percent = item.percents?.[0] ?? 0
    const yValue = percent * 100
    return [i, yValue, yValue.toFixed(0) + '%']
  })
  return {
    type: 'custom',
    name: titles[0],
    renderItem: (params: any, api: any) => {
      const xIndex = api.value(0)
      const yValue = api.value(1)
      const labelText = api.value(2)
      if (!yValue) return
      const xCenter = api.coord([xIndex, 0])[0]
      const yCoord = api.coord([0, yValue])[1]
      const barWidth = api.size([1, 0])[0]
      const xStart = Number(xCenter) - Number(barWidth) / 2
      const xEnd = Number(xCenter) + Number(barWidth) / 2
      return {
        type: 'group',
        children: [
          {
            type: 'line',
            shape: {
              x1: xStart + 25,
              y1: yCoord,
              x2: xEnd - 25,
              y2: yCoord
            },
            style: {
              stroke: barColors[0],
              lineWidth: 12
            }
          },
          {
            type: 'text',
            style: {
              text: labelText,
              x: xStart + 10,
              y: Number(yCoord) + 7,
              fill: barColors[0],
              font: '14px',
              fontWeight: '600',
              textAlign: 'center',
              textVerticalAlign: 'bottom'
            }
          }
        ]
      }
    },
    data: customData,
    z: 10
  }
}

const BarChart: React.FC<BarChartProps> = ({
  titleComponent,
  statisticBox,
  chartKey,
  showPercents = false,
  titles = [],
  report,
  showSlice = false
}) => {
  const theme = useTheme()
  const { selectedTrend } = useReportsContext()
  const optionsData = useMemo(() => {
    const options: OptionType[] = report?.options ?? []
    const keys = options.map(option => option.name)
    const groupCount = options[0]?.counts?.length ?? 1
    const filteredOptions = getFilteredOptions(options, showSlice)
    const series = buildBarSeries({ groupCount, titles, filteredOptions, showPercents, theme, showSlice, barColors: getBarChartColors(selectedTrend) })
    const customLineSeries = buildCustomLineSeries(titles, options, getBarChartColors(selectedTrend))

    return {
      color: getBarChartColors(selectedTrend),
      grid: { left: 60, right: 20, top: 70, bottom: groupCount > 3 ? 100 : 60 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any) =>
          formatTooltip({ params, titles, keys, options, showPercents })
      },
      legend: {
        show: groupCount > 1,
        bottom: 10,
        type: 'plain',
        itemWidth: 14,
        itemHeight: 14,
        data: titles,
        itemGap: 20,
        textStyle: { color: theme.palette.text.secondary, fontSize: 14 }
      },
      xAxis: {
        type: 'category',
        data: keys,
        position: 'top',
        axisLabel: {
          color: theme.palette.text.secondary,
          fontSize: 14,
          fontWeight: 500,
          align: 'center',
          interval: 0,
          margin: 40,
          overflow: 'truncate',
          width: 110,
          showMaxLabel: true,
          showMinLabel: true
        },
        axisTick: { show: false },
        axisLine: { show: false },
        z: 10
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: showPercents ? 100 : undefined,
        splitLine: { show: true },
        axisLabel: {
          show: showPercents,
          formatter: '{value}%',
          color: theme.palette.text.secondary,
          fontSize: 14,
          margin: 20
        },
        axisLine: { show: false },
        axisTick: { show: false }
      },
      series: [

        ...(showSlice ? [customLineSeries] : []),
        ...series.map(s => ({
          ...s,
          barCategoryGap: 50,
          barGap: '0%'
        }))
      ]
    }
  }, [report, titles, showPercents, theme, showSlice])

  return (
    <Card key={chartKey}>
      <CardContent>
        {titleComponent}
        <ReactECharts
          key={`${chartKey}-${showPercents ? 'pct' : 'raw'}`}
          option={optionsData}
          style={{ width: '100%', height: '400px', maxHeight: 500 }}
          notMerge
          lazyUpdate
          opts={{ renderer: 'svg', width: 'auto', height: 'auto' }}
        />
        {statisticBox}
      </CardContent>
    </Card>
  )
}

export default BarChart
